/**
 * /admin/hustlechat/reports — admin review queue for HustleChat abuse
 * reports.  Admin can dismiss, warn, restrict the sender, or ban.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  AlertTriangle, ShieldX, ShieldAlert, CheckCircle2, Slash,
  MessageCircle, Loader2, Clock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
function authHeaders() {
  const t = localStorage.getItem("token") || localStorage.getItem("authToken");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function AdminChatReportsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [resolving, setResolving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/hustlechat/reports`,
        { headers: authHeaders() });
      setItems(r.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const resolve = async (id, decision) => {
    const note = decision === "dismiss" ? "" :
      window.prompt(`Add a note for "${decision}" (optional):`, "") || "";
    setResolving(id);
    try {
      await axios.post(
        `${API}/api/admin/hustlechat/reports/${id}/resolve`,
        { decision, note },
        { headers: authHeaders() },
      );
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
    setResolving(null);
  };

  return (
    <div style={s.root} data-testid="admin-chat-reports">
      <header style={s.header}>
        <h1 style={s.h1}>
          <AlertTriangle size={20} /> HustleChat report queue
        </h1>
        <button onClick={load} style={s.refresh}>Refresh</button>
      </header>

      {loading && (
        <div style={s.empty}><Loader2 className="spin" /> Loading…</div>
      )}
      {err && <div style={s.warn}>{err}</div>}

      {!loading && items.length === 0 && (
        <div style={s.empty}>
          <CheckCircle2 size={32} color="#16a34a" />
          <p>No outstanding reports. Queue is clean.</p>
        </div>
      )}

      {items.map(r => (
        <div key={r.id} style={s.card}
             data-testid={`admin-report-${r.id}`}>
          <div style={s.cardHead}>
            <span style={r.resolved ? s.tagResolved : s.tagOpen}>
              {r.resolved ? "Resolved" : "Open"}
            </span>
            {r.auto_category && (
              <span style={s.tagCat}>
                <ShieldAlert size={11} /> {r.auto_category}
              </span>
            )}
            <span style={s.ts}>
              <Clock size={11} />{" "}
              {new Date(r.ts).toLocaleString("en-AU")}
            </span>
          </div>
          <div style={s.row}>
            <div style={s.label}>Conversation</div>
            <div style={s.val}>{r.conv_id}</div>
          </div>
          <div style={s.row}>
            <div style={s.label}>Reporter</div>
            <div style={s.val}>{r.reporter_id}</div>
          </div>
          <div style={s.row}>
            <div style={s.label}>Reason</div>
            <div style={s.val}>{r.reason}</div>
          </div>
          <div style={{ ...s.row, alignItems: "flex-start" }}>
            <div style={s.label}>Quoted text</div>
            <div style={{ ...s.val, background: "#fef3c7",
                           border: "1px solid #fde68a", borderRadius: 6,
                           padding: 10, fontFamily: "ui-monospace, monospace",
                           fontSize: 12, lineHeight: 1.5,
                           whiteSpace: "pre-wrap" }}>
              {r.cleartext}
            </div>
          </div>

          {!r.resolved && (
            <div style={s.actions}>
              <button disabled={resolving === r.id}
                      onClick={() => resolve(r.id, "dismiss")}
                      style={{ ...s.btn, ...s.btnNeutral }}
                      data-testid={`admin-resolve-dismiss-${r.id}`}>
                Dismiss
              </button>
              <button disabled={resolving === r.id}
                      onClick={() => resolve(r.id, "warn")}
                      style={{ ...s.btn, ...s.btnWarn }}
                      data-testid={`admin-resolve-warn-${r.id}`}>
                <MessageCircle size={12} /> Warn sender
              </button>
              <button disabled={resolving === r.id}
                      onClick={() => resolve(r.id, "restrict-sender")}
                      style={{ ...s.btn, ...s.btnAmber }}
                      data-testid={`admin-resolve-restrict-${r.id}`}>
                <Slash size={12} /> Restrict
              </button>
              <button disabled={resolving === r.id}
                      onClick={() => resolve(r.id, "ban")}
                      style={{ ...s.btn, ...s.btnBan }}
                      data-testid={`admin-resolve-ban-${r.id}`}>
                <ShieldX size={12} /> Ban + wipe sessions
              </button>
            </div>
          )}
          {r.resolved && (
            <div style={s.resolved}>
              <CheckCircle2 size={13} /> Resolved as{" "}
              <strong>{r.resolution}</strong>
              {r.resolution_note && ` — ${r.resolution_note}`}
            </div>
          )}
        </div>
      ))}

      <style>{`.spin{animation:spin 1s linear infinite}
                @keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const s = {
  root: { padding: 28, maxWidth: 960, margin: "0 auto",
          fontFamily: "Inter, system-ui, sans-serif", color: "#0f172a" },
  header: { display: "flex", justifyContent: "space-between",
            alignItems: "center", marginBottom: 24 },
  h1: { fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 800,
        margin: 0, letterSpacing: "-0.02em",
        display: "inline-flex", alignItems: "center", gap: 8 },
  refresh: { background: "#0f172a", color: "#fbbf24", border: "none",
             padding: "8px 18px", borderRadius: 999, cursor: "pointer",
             fontWeight: 700, fontSize: 12 },
  empty: { textAlign: "center", padding: "60px 0", color: "#64748b" },
  warn: { background: "#fee2e2", color: "#991b1b", padding: 12,
          borderRadius: 8, marginBottom: 18 },
  card: { background: "white", border: "1px solid #e8e2d4",
          borderRadius: 14, padding: 18, marginBottom: 14 },
  cardHead: { display: "flex", gap: 8, marginBottom: 12,
              alignItems: "center", flexWrap: "wrap" },
  tagOpen: { background: "#fee2e2", color: "#991b1b", padding: "3px 10px",
             borderRadius: 999, fontSize: 11, fontWeight: 700,
             textTransform: "uppercase", letterSpacing: 0.5 },
  tagResolved: { background: "#dcfce7", color: "#166534",
                  padding: "3px 10px", borderRadius: 999, fontSize: 11,
                  fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: 0.5 },
  tagCat: { display: "inline-flex", alignItems: "center", gap: 4,
            background: "#fef3c7", color: "#92400e", padding: "3px 10px",
            borderRadius: 999, fontSize: 11, fontWeight: 700 },
  ts: { marginLeft: "auto", color: "#64748b", fontSize: 11,
        display: "inline-flex", alignItems: "center", gap: 4 },
  row: { display: "flex", gap: 12, marginBottom: 6, fontSize: 13 },
  label: { color: "#64748b", width: 110, flexShrink: 0 },
  val: { color: "#0f172a", flex: 1, wordBreak: "break-all" },
  actions: { display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" },
  btn: { display: "inline-flex", alignItems: "center", gap: 4,
         padding: "8px 16px", borderRadius: 999, border: "none",
         cursor: "pointer", fontWeight: 700, fontSize: 12 },
  btnNeutral: { background: "#f1f5f9", color: "#475569" },
  btnWarn: { background: "#fef3c7", color: "#92400e" },
  btnAmber: { background: "#fed7aa", color: "#9a3412" },
  btnBan: { background: "#fee2e2", color: "#991b1b" },
  resolved: { marginTop: 12, color: "#16a34a", fontSize: 12,
              display: "inline-flex", alignItems: "center", gap: 6 },
};
